/* eslint-disable no-unused-vars */
import { useContext, useState, useEffect, memo } from "react";
import {
  Box,
  Tab,
  IconButton,
  CircularProgress,
  Divider,
  Stack,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  KeyboardDoubleArrowDown,
  KeyboardDoubleArrowUp,
} from "@mui/icons-material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { AppContext } from "../context";
import { pathJoinUrl } from "@smfa/utils/src";
import { addeditSlider } from "../actions/home.actions";
import LimitedTextField from "../components/common/LimitedTextField";
import FileUploadArea from "../components/common/FileUploadArea";
import FormActionBar from "../components/common/FormActionBar";
import { useSliderData } from "../hooks/useSliderData";
import DataTable from "../components/common/DataTable";
import SearchControls from "../components/common/SearchControls";
import TablePagination from "../components/common/TablePagination";
import ToolBody from "../components/common/ToolBody";
import DeleteModal from "../ui-components/DeleteModal";
import IOSSwitch from "../components/common/IOSSwitch";

const ORDER_COLUMN_WIDTH = 56;
const BANNER_COLUMN_WIDTH = 180;
const ACTION_COLUMN_WIDTH = 104;

/**
 *
 * @returns {JSX.Element}
 * @component
 */
const Home = () => {
  const { storage } = useContext(AppContext);

  // eslint-disable-next-line no-unused-vars
  const [sliderList, setSliderList] = useState([]);

  // Use the slider data hook for data fetching and state management
  const sliderData = useSliderData({
    searchText: "",
    searchType: "lebel",
  });

  // Destructure values from sliderData hook with defaults to prevent undefined errors
  const {
    data = [],
    loading = false,
    fetchData,
    totalPage = 0,
    totalCount = 0,
    pageNo = 1,
    perPage = 50,
    search = { text: "", type: "lebel" }, // Default object to prevent undefined
    handleSearchChange = () => {},
    setSearchText = () => {},
    setSearchType = () => {},
    submitSearch = () => {},
    clearSearch = () => {},
    toggleStatus = () => {},
    toggleLoading = null,
    reorderLoading = null,
    reorderSliderItem = () => Promise.resolve(null),
    deleteSliderItem = () => Promise.resolve({ data: { status: false } }),
  } = sliderData || {};

  // Local component state
  // eslint-disable-next-line no-unused-vars
  const [firstLoading, setFirstLoading] = useState(true);
  const [loader, setLoader] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  var addSlider_default = {
    slider_id: 0,
    image: "",
    image_name: "",
    image_error: false,
    image_mobile: "",
    image_mobile_name: "",
    image_mobile_error: false,
    title: "",
    title_maxlenth: 60,
    title_error: false,
    short_des: "",
    short_des_maxlenth: 300,
    short_des_error: false,
    lebel: "",
    lebel_maxlenth: 50,
    lebel_error: false,
  };

  // eslint-disable-next-line no-unused-vars
  const [loaderId, setLoaderId] = useState("");
  const [addSlider, setSlider] = useState(addSlider_default);
  const [value, setValue] = useState("1");
  const [deskimage, setDeskImage] = useState("");
  const [mobimage, setMobImage] = useState("");
  const [deleteItem, setDeleteItem] = useState(null);

  // Search options for the 2nd tab
  const searchOptions = [
    { value: "lebel", label: "Label" },
    { value: "title", label: "Title" },
    { value: "short_des", label: "Description" },
  ];

  // Table columns configuration
  const columns = [
    {
      field: "order",
      label: "Order",
      width: ORDER_COLUMN_WIDTH,
      minWidth: ORDER_COLUMN_WIDTH,
      maxWidth: ORDER_COLUMN_WIDTH,
      align: "left",
      compact: true,
      cellSx: {
        whiteSpace: "nowrap",
        verticalAlign: "top",
      },
      render: (row, index) => {
        const hasSearch = Boolean(search?.text?.trim());
        const canReorder = !hasSearch;
        const isFirst = canReorder && pageNo === 1 && index === 0;
        const isLast =
          canReorder && pageNo === totalPage && index === data.length - 1;
        const rowLoading = reorderLoading === row.slider_id;
        const disabled =
          !canReorder || (reorderLoading !== null && !rowLoading);

        const moveButton = (direction, label, icon, edgeDisabled) => (
          <Tooltip key={direction} title={label}>
            <span>
              <IconButton
                aria-label={label}
                onClick={() => reorderSliderItem(row.slider_id, direction)}
                disabled={rowLoading || disabled || edgeDisabled}
                size="small"
              >
                {icon}
              </IconButton>
            </span>
          </Tooltip>
        );

        return rowLoading ? (
          <CircularProgress size={24} />
        ) : (
          <Stack spacing={0} sx={{ alignItems: "flex-start" }}>
            {moveButton(
              "first",
              "Move banner to top",
              <KeyboardDoubleArrowUp />,
              isFirst,
            )}
            {moveButton("up", "Move banner up", <KeyboardArrowUp />, isFirst)}
            {moveButton(
              "down",
              "Move banner down",
              <KeyboardArrowDown />,
              isLast,
            )}
            {moveButton(
              "last",
              "Move banner to bottom",
              <KeyboardDoubleArrowDown />,
              isLast,
            )}
          </Stack>
        );
      },
    },
    {
      field: "labelTitle",
      label: "Label/Title",
      cellSx: { verticalAlign: "top" },
      render: (row) => (
        <Stack
          divider={
            <Divider
              flexItem
              sx={{
                borderColor: "text.secondary",
                borderBottomWidth: 2,
              }}
            />
          }
          spacing={0.5}
        >
          <Box>{row.lebel}</Box>
          <Box>{row.title}</Box>
        </Stack>
      ),
    },
    {
      field: "short_des",
      label: "Short Description",
    },
    {
      field: "banners",
      label: "Banners",
      width: BANNER_COLUMN_WIDTH,
      minWidth: BANNER_COLUMN_WIDTH,
      maxWidth: BANNER_COLUMN_WIDTH,
      compact: true,
      render: (row) => (
        <Stack
          divider={<Divider flexItem />}
          spacing={1}
          sx={{ alignItems: "flex-start" }}
        >
          {row.image && (
            <Box>
              <Box
                component="span"
                sx={{
                  display: "block",
                  color: "text.secondary",
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  mb: 0.25,
                }}
              >
                Desktop
              </Box>
              <img
                src={pathJoinUrl(storage?.[0]?.imageEndUrl || "", row.image)}
                alt="Desktop Banner"
                style={{ display: "block", maxWidth: 100, maxHeight: 60 }}
              />
            </Box>
          )}
          {row.image_mobile && (
            <Box>
              <Box
                component="span"
                sx={{
                  display: "block",
                  color: "text.secondary",
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  mb: 0.25,
                }}
              >
                Mobile
              </Box>
              <img
                src={pathJoinUrl(
                  storage?.[0]?.imageEndUrl || "",
                  row.image_mobile,
                )}
                alt="Mobile Banner"
                style={{ display: "block", maxWidth: 75, maxHeight: 45 }}
              />
            </Box>
          )}
        </Stack>
      ),
    },
    {
      field: "actions",
      label: "Action",
      width: ACTION_COLUMN_WIDTH,
      minWidth: ACTION_COLUMN_WIDTH,
      maxWidth: ACTION_COLUMN_WIDTH,
      align: "right",
      compact: true,
      sticky: "right",
      cellSx: {
        whiteSpace: "nowrap",
        verticalAlign: "top",
      },
      render: (row) => (
        <Stack spacing={0.5} sx={{ alignItems: "flex-end" }}>
          <Stack direction="row" spacing={0.5}>
            <IconButton
              aria-label="Edit banner"
              onClick={(e) => editLocation(e, row)}
              color="primary"
              size="small"
            >
              <EditIcon />
            </IconButton>
            <IconButton
              aria-label="Delete banner"
              onClick={() => requestDelete(row)}
              color="error"
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
          {toggleLoading === row.slider_id ? (
            <CircularProgress size={24} />
          ) : (
            <Tooltip title="Enable or disable this banner on the home page">
              <FormControlLabel
                control={
                  <IOSSwitch
                    checked={row.slider_status === "active"}
                    onChange={() =>
                      sliderData.toggleStatus(
                        row.slider_id,
                        row.slider_status !== "active",
                      )
                    }
                    slotProps={{
                      input: {
                        "aria-label": "Enable or disable banner",
                      },
                    }}
                  />
                }
                label=""
              />
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  const onImageChange = (event) => {
    setSuccessMsg("");
    setErrorMsg("");

    const file = event.target.files?.[0];
    if (file) {
      log.debug(file.name);
      setDeskImage(URL.createObjectURL(file));
      setSlider((prev) => ({
        ...prev,
        image: file,
        image_name: file.name,
        image_error: false,
      }));
    }
  };

  const onMobImageChange = (event) => {
    setSuccessMsg("");
    setErrorMsg("");

    const file = event.target.files?.[0];
    if (file) {
      setMobImage(URL.createObjectURL(file));
      setSlider((prev) => ({
        ...prev,
        image_mobile: file,
        image_mobile_name: file.name,
        image_mobile_error: false,
      }));
    }
  };

  const clearUpload = (field, clearPreview) => {
    setSlider((prev) => ({
      ...prev,
      [field]: "",
      [`${field}_name`]: "",
      [`${field}_error`]: false,
    }));
    clearPreview("");
  };

  const handleChange = (event, newValue) => {
    setDeskImage("");
    setMobImage("");
    setSlider(addSlider_default);
    setSuccessMsg("");
    setErrorMsg("");
    setValue(newValue);
  };

  const handleChangeedit = (event, newValue) => {
    setSuccessMsg("");
    setErrorMsg("");
    setValue(newValue);
  };

  useEffect(() => {
    // This effect was causing duplicate data fetching since we're already
    // fetching data in the useSliderData hook
    // Instead of making another API call, we'll just update the display state
    setFirstLoading(false);

    // Log to track component lifecycle
    log.debug(
      "Home component mounted, slider data managed by useSliderData hook",
    );

    // Cleanup function
    return () => {
      log.debug("Home component unmounted");
    };
  }, []);

  const addSliderInfo = (event) => {
    setSuccessMsg("");
    setErrorMsg("");

    var custInfo = { ...addSlider };
    log.debug(event.target.value);
    if (event.target.name === "title") {
      if (event.target.value.length <= addSlider.title_maxlenth) {
        custInfo.title = event.target.value;
        custInfo.title_error = false;
      }
    }
    if (event.target.name === "short_des") {
      if (event.target.value.length <= addSlider.short_des_maxlenth) {
        custInfo.short_des = event.target.value;
        custInfo.short_des_error = false;
      }
    }
    if (event.target.name === "lebel") {
      if (event.target.value.length <= addSlider.lebel_maxlenth) {
        custInfo.lebel = event.target.value;
        custInfo.lebel_error = false;
      }
    }
    setSlider(custInfo);
  };

  const submitLocation = () => {
    setLoader(true);
    setSuccessMsg("");
    setErrorMsg("");

    // Validation code
    var custInfo = { ...addSlider };

    if (addSlider.lebel === "") {
      custInfo.lebel_error = true;
      setSlider(custInfo);
      setErrorMsg("Please enter label");
      setLoader(false);
      return false;
    }

    if (addSlider.title === "") {
      custInfo.title_error = true;
      setSlider(custInfo);
      setErrorMsg("Please enter title");
      setLoader(false);
      return false;
    }

    if (addSlider.short_des === "") {
      custInfo.short_des_error = true;
      setSlider(custInfo);
      setErrorMsg("Please enter a short description");
      setLoader(false);
      return false;
    }

    // For new entries, only the desktop banner is required.
    if (addSlider.slider_id === 0) {
      if (!addSlider.image) {
        custInfo.image_error = true;
        setSlider(custInfo);
        setErrorMsg("Please upload desktop banner image");
        setLoader(false);
        return false;
      }
    }

    // Create FormData object
    const formData = new FormData();
    formData.append("slider_id", addSlider.slider_id);

    // Handle images for new vs edit operations
    if (addSlider.image instanceof File) {
      log.debug("Appending desktop image as file");
      formData.append("image", addSlider.image);
    } else if (addSlider.slider_id !== 0) {
      log.debug("Using existing desktop image name:", addSlider.image_name);
      formData.append("image_name", addSlider.image_name);
    }

    if (addSlider.image_mobile instanceof File) {
      log.debug("Appending mobile image as file");
      formData.append("image_mobile", addSlider.image_mobile);
    } else if (addSlider.slider_id !== 0) {
      log.debug(
        "Using existing mobile image name:",
        addSlider.image_mobile_name,
      );
      formData.append("image_mobile_name", addSlider.image_mobile_name);
    }

    formData.append("title", addSlider.title);
    formData.append("short_des", addSlider.short_des);
    formData.append("lebel", addSlider.lebel);

    // Debug FormData contents properly
    for (let pair of formData.entries()) {
      log.debug(
        pair[0] +
          ": " +
          (pair[1] instanceof File ? "File: " + pair[1].name : pair[1]),
      );
    }

    // Make API call
    addeditSlider(formData)
      .then((res1) => {
        log.debug("API response:", res1);
        if (res1.data.status === true) {
          // Success handling
          setSuccessMsg(
            addSlider.slider_id === 0
              ? "Slider Added Successfully!"
              : "Slider Updated Successfully!",
          );
          setDeskImage("");
          setMobImage("");
          setSlider(addSlider_default);

          // Refresh the slider list
          fetchData();

          setLoader(false);
          setTimeout(() => {
            const startEvent = new Event("start");
            setSuccessMsg("");
            setErrorMsg("");
            handleChange(startEvent, "2");
          }, 1000);
        } else {
          // Error handling
          setErrorMsg(
            res1.data.message || "Something went wrong, please try again!",
          );
          setLoader(false);
        }
      })
      .catch((error) => {
        log.error("API error:", error);
        setLoader(false);
        setErrorMsg("Network error: " + (error.message || "Unknown error"));
      });
  };

  const editLocation = (e, val) => {
    var locInfo = { ...addSlider_default };
    locInfo.slider_id = val.slider_id;
    locInfo.short_des = val.short_des;
    locInfo.title = val.title;
    locInfo.lebel = val.lebel;
    locInfo.image_name = val.image;
    locInfo.image_mobile_name = val.image_mobile;
    setSlider(locInfo);
    setDeskImage(
      val.image
        ? pathJoinUrl(storage?.[0]?.imageEndUrl || "", val.image)
        : "",
    );
    setMobImage(
      val.image_mobile
        ? pathJoinUrl(storage?.[0]?.imageEndUrl || "", val.image_mobile)
        : "",
    );

    setSuccessMsg("");
    setErrorMsg("");
    const startEvent = new Event("start");

    handleChangeedit(startEvent, "1");
  };

  const resetdoc = () => {
    setDeskImage("");
    setMobImage("");
    setSuccessMsg("");
    setErrorMsg("");
    setLoader(false);
    setSlider(addSlider_default);
  };

  const requestDelete = (row) => {
    setSuccessMsg("");
    setErrorMsg("");
    setDeleteItem(row);
  };

  const handleDeleteSuccess = () => {
    const deletedId = deleteItem?.slider_id;
    setDeleteItem(null);

    if (addSlider.slider_id === deletedId) {
      resetdoc();
      setValue("2");
    }

    setSuccessMsg("Banner deleted successfully!");
  };

  const handleDeleteError = (message) => {
    setErrorMsg(message || "Banner could not be deleted. Please try again.");
  };

  // Handle search input changes
  const handleSearchInputChange = (text) => {
    setSearchText(text);
  };

  // Handle search type changes
  const handleSearchTypeChange = (type) => {
    setSearchType(type);
  };

  // Handle search submission
  const handleSearch = () => {
    submitSearch();
  };

  return (
    <ToolBody>
      <TabContext value={value}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList onChange={handleChange} aria-label="lab API tabs example">
            <Tab
              label={` ${addSlider.slider_id !== 0 ? "Update" : "Add"} Banner Section`}
              value="1"
            />
            <Tab label="Manage Banner Section" value="2" />
          </TabList>
        </Box>
        <TabPanel value="1" sx={{ maxWidth: 700 }}>
          <LimitedTextField
            id="lebel"
            name="lebel"
            label="Label"
            variant="standard"
            value={addSlider.lebel}
            onChange={addSliderInfo}
            maxLength={addSlider.lebel_maxlenth}
            error={addSlider.lebel_error}
            errorText="Please enter Label!"
          />

          <LimitedTextField
            id="title"
            name="title"
            label="Title"
            variant="standard"
            value={addSlider.title}
            onChange={addSliderInfo}
            maxLength={addSlider.title_maxlenth}
            error={addSlider.title_error}
            errorText="Please enter Title!"
          />

          <LimitedTextField
            id="short_des"
            name="short_des"
            label="Short Description"
            placeholder="Short Description"
            multiline
            rows={2}
            variant="standard"
            value={addSlider.short_des}
            onChange={addSliderInfo}
            maxLength={addSlider.short_des_maxlenth}
            error={addSlider.short_des_error}
            errorText="Please enter Short Description!"
          />

          <Stack direction="row" spacing={3}>
            <FileUploadArea
              title="Add Banner Image For Desktop"
              description={
                <>
                  <span>Files types: JPG, JPEG & PNG</span>
                  <br />
                  <span>Best image Resolution: 950px X 650px</span>
                </>
              }
              previewUrl={deskimage}
              fileName={addSlider.image_name}
              onChange={onImageChange}
              deleteRequest={{
                resource: "slider",
                id: addSlider.slider_id,
                field: "image",
              }}
              onRemove={() => clearUpload("image", setDeskImage)}
              accept=".png,.jpg,.jpeg"
              error={addSlider.image_error}
              errorText="Please upload Desktop Banner Image!"
            />

            <FileUploadArea
              title="Add Banner Image For Mobile (Optional)"
              description={
                <>
                  <span>Supported files types: JPG, JPEG & PNG</span>
                  <br />
                  <span>Best image Resolution: 400px X 350px</span>
                </>
              }
              previewUrl={mobimage}
              fileName={addSlider.image_mobile_name}
              onChange={onMobImageChange}
              deleteRequest={{
                resource: "slider",
                id: addSlider.slider_id,
                field: "image_mobile",
              }}
              onRemove={() => clearUpload("image_mobile", setMobImage)}
              accept=".png,.jpg,.jpeg"
            />
          </Stack>

          <FormActionBar
            onSubmit={submitLocation}
            onReset={resetdoc}
            onDelete={
              addSlider.slider_id !== 0
                ? () => requestDelete(addSlider)
                : undefined
            }
            isSubmitting={loader}
            isEdit={addSlider.slider_id !== 0}
            successMessage={successMsg}
            errorMessage={errorMsg}
          />
        </TabPanel>
        <TabPanel value="2">
          <Box sx={{ mb: 2 }}>
            {/* Search controls */}
            <SearchControls
              searchText={search?.text || ""}
              searchType={search?.type || "lebel"}
              searchOptions={searchOptions}
              totalCount={totalCount}
              onSearchChange={handleSearchInputChange}
              onTypeChange={handleSearchTypeChange}
              onSubmit={handleSearch}
              onClear={clearSearch}
            />

            {/* Data table */}
            <DataTable
              columns={columns}
              data={data || []}
              loading={loading}
              keyField="slider_id"
              tableLayout="fixed"
            />

            {/* Pagination */}
            <TablePagination
              page={pageNo}
              totalPages={totalPage}
              loading={loading}
              onChange={(page) => {
                fetchData({ pageNo: page, force: true });
              }}
              entriesPerPage={perPage}
              onEntriesChange={(entries) => {
                fetchData({ perPage: entries, pageNo: 1, force: true });
              }}
            />
          </Box>
        </TabPanel>
      </TabContext>

      <DeleteModal
        open={deleteItem !== null}
        title="Delete Banner"
        message="Are you sure you want to delete this banner?"
        handleClose={() => setDeleteItem(null)}
        handleSuccess={handleDeleteSuccess}
        handleError={handleDeleteError}
        deleteAction={deleteSliderItem}
        data={deleteItem ? { slider_id: deleteItem.slider_id } : null}
      />
    </ToolBody>
  );
};

// Wrap the component with memo to improve stability
export default memo(Home, (prev, next) => {
  // Consider equal if related state hasn't changed
  // This helps prevent unnecessary re-renders
  return true; // Always consider equal unless props are explicitly passed
});
